import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { getVendorIdsForProfile } from "./vendor-membership.server";

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}


const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type BuyerOrder = {
  id: string;
  created_at: string;
  status: string;
  amount_total: number;
  currency: string;
  tracking_number: string | null;
  tracking_carrier: string | null;
  shipped_at: string | null;
  paid_at: string | null;
  invoice_number: string | null;
  invoice_pdf_url: string | null;
  quote_pdf_url: string | null;
  quote_id: string | null;
  quote_group_id: string | null;
  notes: string | null;
  wp_order_number: string | null;
  customer_name: string | null;
  customer_email: string | null;
  shipping_address: ShippingAddress | null;
  items: Array<{
    id: string;
    title: string;
    quantity: number;
    unit_price: number;
    line_total: number;
    product_id: string | null;
    vendor_id: string | null;
    vendor_name: string | null;
    vendor_slug: string | null;
  }>;
};

const ORDER_SELECT = "id, created_at, status, amount_total, currency, customer_name, customer_email, shipping_address, tracking_number, tracking_carrier, shipped_at, paid_at, invoice_number, invoice_pdf_url, quote_pdf_url, quote_id, quote_group_id, notes, wp_order_number";

export const getMyOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ orders: BuyerOrder[] }> => {
    const { userId, claims } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { ensureLinkedProfileId } = await import("./profile-link.server");
    const profileId = await ensureLinkedProfileId(
      supabaseAdmin as never,
      userId,
      (claims.email as string | undefined) ?? null,
    );
    if (!profileId) return { orders: [] };

    const { data: orders } = await supabaseAdmin
      .from("orders")
      .select(ORDER_SELECT)
      .eq("profile_id", profileId)
      .order("created_at", { ascending: false })
      .limit(100);
    const oids = (orders ?? []).map((o) => o.id);
    if (oids.length === 0) return { orders: [] };

    const { data: items } = await supabaseAdmin
      .from("order_items")
      .select("id, order_id, title, quantity, unit_price, line_total, product_id, vendor_id, vendor:vendors(store_name, slug)")
      .in("order_id", oids);

    const byOrder: Record<string, BuyerOrder["items"]> = {};
    for (const it of items ?? []) {
      (byOrder[it.order_id as string] ||= []).push({
        id: it.id as string,
        title: it.title as string,
        quantity: it.quantity as number,
        unit_price: Number(it.unit_price),
        line_total: Number(it.line_total),
        product_id: (it.product_id as string | null) ?? null,
        vendor_id: (it.vendor_id as string | null) ?? null,
        vendor_name: ((it as unknown as { vendor: { store_name: string } | null }).vendor?.store_name) ?? null,
        vendor_slug: ((it as unknown as { vendor: { slug: string } | null }).vendor?.slug) ?? null,
      });
    }

    return {
      orders: (orders ?? []).map((o) => ({
        ...(o as unknown as BuyerOrder),
        amount_total: Number(o.amount_total),
        shipping_address: (o.shipping_address as ShippingAddress | null) ?? null,
        items: byOrder[o.id as string] ?? [],
      })),
    };
  });

export type ShippingAddress = {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  postal_code?: string;
  state?: string;
  country?: string;
  phone?: string;
};

export type VendorOrderItem = {
  id: string;
  order_id: string;
  created_at: string;
  order_status: string;
  customer_name: string | null;
  customer_email: string;
  title: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  product_id: string | null;
  shipping_address: ShippingAddress | null;
  tracking_number: string | null;
  tracking_carrier: string | null;
  invoice_number: string | null;
  notes: string | null;
  quote_id: string | null;
};

export const getVendorOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) return { items: [] as VendorOrderItem[], isAdmin: false };

    const { data: roleRows } = await supabaseAdmin
      .from("user_roles").select("role").eq("profile_id", profile.id);
    const isAdmin = (roleRows ?? []).some((r) => String(r.role) === "admin");

    let query = supabaseAdmin
      .from("order_items")
      .select("id, order_id, title, quantity, unit_price, line_total, product_id, order:orders(id, created_at, status, customer_name, customer_email, shipping_address, tracking_number, tracking_carrier, invoice_number, notes, quote_id)")
      .order("created_at", { ascending: false })
      .limit(isAdmin ? 5000 : 3000);


    if (!isAdmin) {
      const vendorIds = await getVendorIdsForProfile(supabaseAdmin, profile.id);
      if (vendorIds.length === 0) return { items: [] as VendorOrderItem[], isAdmin: false };
      query = query.in("vendor_id", vendorIds);
    }


    const { data: items } = await query;

    type OrderRel = { id: string; created_at: string; status: string; customer_name: string | null; customer_email: string; shipping_address: ShippingAddress | null; tracking_number: string | null; tracking_carrier: string | null; invoice_number: string | null; notes: string | null; quote_id: string | null };
    const mapped: VendorOrderItem[] = (items ?? []).map((it) => {
      const o = (it as unknown as { order: OrderRel | null }).order;
      return {
        id: it.id as string,
        order_id: o?.id ?? (it.order_id as string),
        created_at: o?.created_at ?? "",
        order_status: String(o?.status ?? "pending"),
        customer_name: o?.customer_name ?? null,
        customer_email: o?.customer_email ?? "",
        title: it.title as string,
        quantity: it.quantity as number,
        unit_price: Number(it.unit_price),
        line_total: Number(it.line_total),
        product_id: (it.product_id as string | null) ?? null,
        shipping_address: o?.shipping_address ?? null,
        tracking_number: o?.tracking_number ?? null,
        tracking_carrier: o?.tracking_carrier ?? null,
        invoice_number: o?.invoice_number ?? null,
        notes: o?.notes ?? null,
        quote_id: o?.quote_id ?? null,
      };
    });
    mapped.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return { items: mapped, isAdmin };

  });

async function assertVendorOwnsOrder(profileId: string, orderId: string, isAdmin: boolean) {
  if (isAdmin) return;
  const supabaseAdmin = await getSupabaseAdmin();
  const vendorIds = await getVendorIdsForProfile(supabaseAdmin, profileId);
  if (vendorIds.length === 0) throw new Error("Ni dovoljenja.");
  const { data: matches } = await supabaseAdmin
    .from("order_items").select("id")
    .eq("order_id", orderId)
    .in("vendor_id", vendorIds)
    .limit(1);
  if ((matches ?? []).length === 0) throw new Error("Ni dovoljenja.");
}


export const updateOrderStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({
    order_id: z.string().uuid(),
    status: z.enum(["quote_requested", "quote_sent", "pending", "paid", "shipped", "delivered", "canceled", "refunded"]),
  }).parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");

    const { data: roleRow } = await supabaseAdmin
      .from("user_roles").select("role").eq("profile_id", profile.id);
    const isAdmin = (roleRow ?? []).some((r) => String(r.role) === "admin");

    await assertVendorOwnsOrder(profile.id, data.order_id, isAdmin);

    const update: Record<string, unknown> = { status: data.status };

    // On marking paid: stamp paid_at, generate invoice number, decrement stock
    if (data.status === "paid") {
      update.paid_at = new Date().toISOString();

      const { data: existing } = await supabaseAdmin
        .from("orders").select("invoice_number, status").eq("id", data.order_id).maybeSingle();

      if (existing && !existing.invoice_number) {
        const { data: inv } = await supabaseAdmin.rpc("next_invoice_number");
        if (inv) update.invoice_number = inv as string;
      }

      // Decrement stock atomically (only on first transition to paid)
      if (existing && existing.status !== "paid" && existing.status !== "shipped" && existing.status !== "delivered") {
        const { data: items } = await supabaseAdmin
          .from("order_items")
          .select("product_id, quantity, title")
          .eq("order_id", data.order_id);
        for (const it of items ?? []) {
          if (!it.product_id) continue;
          const { data: ok } = await supabaseAdmin.rpc("decrement_stock", {
            _product_id: it.product_id,
            _qty: it.quantity,
          });
          if (ok === false) {
            throw new Error(`Premalo zaloge za "${it.title}".`);
          }
        }
      }
    }

    const { error } = await supabaseAdmin
      .from("orders")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update(update as any)
      .eq("id", data.order_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const markOrderShipped = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({
    order_id: z.string().uuid(),
    tracking_number: z.string().trim().max(200).optional().nullable(),
    tracking_carrier: z.string().trim().max(100).optional().nullable(),
  }).parse(input))
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id").eq("auth_user_id", context.userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");
    const { data: roleRow } = await supabaseAdmin
      .from("user_roles").select("role").eq("profile_id", profile.id);
    const isAdmin = (roleRow ?? []).some((r) => String(r.role) === "admin");
    await assertVendorOwnsOrder(profile.id, data.order_id, isAdmin);

    const { error } = await supabaseAdmin
      .from("orders")
      .update({
        status: "shipped",
        shipped_at: new Date().toISOString(),
        tracking_number: data.tracking_number || null,
        tracking_carrier: data.tracking_carrier || null,
      })
      .eq("id", data.order_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ============================================================
// Convert cart → quote request (replaces Stripe checkout)
// ============================================================

const cartItemSchema = z.object({
  product_id: z.string().min(1).max(100),
  quantity: z.number().int().min(1).max(100),
});

export const requestQuoteFromCart = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({
    items: z.array(cartItemSchema).min(1).max(50),
    notes: z.string().trim().max(2000).optional().nullable(),
    shipping_address: z.object({
      name: z.string().trim().min(2).max(200),
      line1: z.string().trim().min(2).max(200),
      line2: z.string().trim().max(200).optional().nullable(),
      city: z.string().trim().min(2).max(100),
      postal_code: z.string().trim().min(2).max(20),
      country: z.string().trim().min(2).max(100).default("Slovenija"),
      phone: z.string().trim().max(50).optional().nullable(),
    }),
  }).parse(input))
  .handler(async ({ data, context }) => {
    const { rateLimit } = await import("@/lib/rate-limit.server");
    await rateLimit(`cart-order:${context.userId}`, 5, 60);

    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id, email, first_name, last_name, display_name").eq("auth_user_id", context.userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja.");

    // Resolve products from DB
    const uuidIds = data.items.filter((i) => UUID_RE.test(i.product_id)).map((i) => i.product_id);
    if (uuidIds.length === 0) throw new Error("Košarica ne vsebuje veljavnih izdelkov.");

    const { data: products } = await supabaseAdmin
      .from("products")
      .select("id, title, price, sale_price, vendor_id, stock_quantity, in_stock")
      .in("id", uuidIds);

    const pMap = new Map((products ?? []).map((p) => [p.id as string, p]));

    // Validate stock and group by vendor
    type GroupItem = { product_id: string; title: string; price: number; sale_price: number | null; qty: number; vendor_id: string };
    const byVendor = new Map<string, GroupItem[]>();
    let amountTotal = 0;

    for (const i of data.items) {
      const p = pMap.get(i.product_id);
      if (!p) throw new Error("Izdelek ni najden.");
      if (!p.in_stock) throw new Error(`Izdelek "${p.title}" trenutno ni na zalogi.`);
      if (p.stock_quantity != null && i.quantity > p.stock_quantity) {
        throw new Error(`Za "${p.title}" je na zalogi le ${p.stock_quantity} kos.`);
      }
      if (!p.vendor_id) throw new Error(`Izdelek "${p.title}" nima dodeljenega prodajalca.`);

      const unit = Number(p.sale_price ?? p.price ?? 0);
      amountTotal += unit * i.quantity;

      const list = byVendor.get(p.vendor_id) ?? [];
      list.push({
        product_id: p.id as string,
        title: p.title as string,
        price: Number(p.price ?? 0),
        sale_price: p.sale_price != null ? Number(p.sale_price) : null,
        qty: i.quantity,
        vendor_id: p.vendor_id as string,
      });
      byVendor.set(p.vendor_id, list);
    }

    const buyerName = profile.display_name || [profile.first_name, profile.last_name].filter(Boolean).join(" ") || data.shipping_address.name;

    const orderIds: string[] = [];
    const groupId = crypto.randomUUID();

    // Create one order + quote per vendor
    for (const [vendorId, items] of byVendor.entries()) {
      const orderAmount = items.reduce((s, i) => s + Number(i.sale_price ?? i.price) * i.qty, 0);

      const { data: order, error: orderErr } = await supabaseAdmin
        .from("orders")
        .insert({
          profile_id: profile.id,
          customer_email: profile.email,
          customer_name: buyerName,
          amount_total: orderAmount,
          currency: "eur",
          status: "quote_requested",
          shipping_address: data.shipping_address,
          notes: data.notes || null,
          quote_group_id: groupId,
        })
        .select("id")
        .single();
      if (orderErr) throw new Error(orderErr.message);

      const orderItemRows = items.map((i) => ({
        order_id: order.id,
        product_id: i.product_id,
        vendor_id: vendorId,
        title: i.title,
        unit_price: i.price,
        sale_price: i.sale_price,
        quantity: i.qty,
        line_total: Number(i.sale_price ?? i.price) * i.qty,
      }));
      const { error: itemsErr } = await supabaseAdmin.from("order_items").insert(orderItemRows);
      if (itemsErr) throw new Error(itemsErr.message);

      // Create draft quote linked to this order
      const subtotal = orderAmount;
      const { data: quote, error: qErr } = await supabaseAdmin
        .from("quotes")
        .insert({
          vendor_id: vendorId,
          buyer_profile_id: profile.id,
          buyer_email: profile.email,
          buyer_name: buyerName,
          title: `Povpraševanje #${(order.id as string).slice(0, 8)}`,
          notes: data.notes || null,
          subtotal,
          shipping: 0,
          total: subtotal,
          status: "draft",
          order_id: order.id,
        })
        .select("id")
        .single();
      if (qErr) throw new Error(qErr.message);

      const quoteItemRows = items.map((i, idx) => ({
        quote_id: quote.id,
        title: i.title,
        quantity: i.qty,
        unit_price: Number(i.sale_price ?? i.price),
        line_total: Number(i.sale_price ?? i.price) * i.qty,
        position: idx,
      }));
      await supabaseAdmin.from("quote_items").insert(quoteItemRows);

      // Link quote back to order
      await supabaseAdmin.from("orders").update({ quote_id: quote.id }).eq("id", order.id);

      orderIds.push(order.id as string);

      // Best-effort: notify vendor about new inquiry (owner + all shop members)
      try {
        const { data: v } = await supabaseAdmin
          .from("vendors")
          .select("store_name, profile:profiles(email, display_name, first_name)")
          .eq("id", vendorId)
          .maybeSingle();
        const recipientEmails = new Set<string>();
        const ownerEmail = (v?.profile as { email?: string } | null)?.email;
        if (ownerEmail) recipientEmails.add(ownerEmail.toLowerCase());

        // Include all vendor members (e.g. tajnistvo@) so the whole team is notified.
        const { data: members } = await supabaseAdmin
          .from("vendor_members")
          .select("profile:profiles(email)")
          .eq("vendor_id", vendorId);
        for (const m of members ?? []) {
          const em = (m as { profile: { email?: string } | null }).profile?.email;
          if (em) recipientEmails.add(em.toLowerCase());
        }

        if (recipientEmails.size > 0) {
          const vp = v?.profile as { display_name?: string | null; first_name?: string | null } | null;
          const vendorName = vp?.display_name || vp?.first_name || v?.store_name || "";
          const { sendInquiryEmail } = await import("@/lib/mail.server");
          for (const to of recipientEmails) {
          await sendInquiryEmail({
            to,
            vendorName,
            storeName: v?.store_name ?? "kupish.si",
            buyerName,
            buyerEmail: profile.email,
            buyerPhone: data.shipping_address.phone ?? null,
            shippingAddress: {
              line1: data.shipping_address.line1,
              postal_code: data.shipping_address.postal_code,
              city: data.shipping_address.city,
              country: data.shipping_address.country,
            },
            notes: data.notes || null,
            total: orderAmount,
            currency: "EUR",
            items: items.map((i) => ({
              title: i.title,
              quantity: i.qty,
              unit_price: Number(i.sale_price ?? i.price),
              line_total: Number(i.sale_price ?? i.price) * i.qty,
            })),
          });
          }
        }
      } catch (err) {
        console.error("[requestQuoteFromCart] vendor notify failed:", err);
      }
    }

    return { ok: true, order_ids: orderIds, count: orderIds.length, total: amountTotal, group_id: groupId };
  });

export const getOrderGroup = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ group_id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }): Promise<{ orders: BuyerOrder[] }> => {
    const supabaseAdmin = await getSupabaseAdmin();
    const { data: profile } = await supabaseAdmin
      .from("profiles").select("id").eq("auth_user_id", context.userId).maybeSingle();
    if (!profile) return { orders: [] };

    const { data: orders } = await supabaseAdmin
      .from("orders")
      .select(ORDER_SELECT)
      .eq("profile_id", profile.id)
      .eq("quote_group_id", data.group_id)
      .order("created_at", { ascending: true });
    const oids = (orders ?? []).map((o) => o.id);
    if (oids.length === 0) return { orders: [] };

    const { data: items } = await supabaseAdmin
      .from("order_items")
      .select("id, order_id, title, quantity, unit_price, line_total, product_id, vendor_id, vendor:vendors(store_name, slug)")
      .in("order_id", oids);

    const byOrder: Record<string, BuyerOrder["items"]> = {};
    for (const it of items ?? []) {
      (byOrder[it.order_id as string] ||= []).push({
        id: it.id as string,
        title: it.title as string,
        quantity: it.quantity as number,
        unit_price: Number(it.unit_price),
        line_total: Number(it.line_total),
        product_id: (it.product_id as string | null) ?? null,
        vendor_id: (it.vendor_id as string | null) ?? null,
        vendor_name: ((it as unknown as { vendor: { store_name: string } | null }).vendor?.store_name) ?? null,
        vendor_slug: ((it as unknown as { vendor: { slug: string } | null }).vendor?.slug) ?? null,
      });
    }

    return {
      orders: (orders ?? []).map((o) => ({
        ...(o as unknown as BuyerOrder),
        amount_total: Number(o.amount_total),
        shipping_address: (o.shipping_address as ShippingAddress | null) ?? null,
        items: byOrder[o.id as string] ?? [],
      })),
    };
  });

